import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { IN_APP_TOOLS, IN_APP_CATEGORIES, type InAppTool } from '../../config/inAppTools';
import { useWorkbenchContext, buildContextParams } from '../../hooks/useWorkbenchContext';

interface ToolSwitcherProps {
  currentToolId: string;
}

const ToolSwitcher: React.FC<ToolSwitcherProps> = ({ currentToolId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const ctx = useWorkbenchContext();

  const currentTool = IN_APP_TOOLS.find(t => t.id === currentToolId);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSelect = (tool: InAppTool) => {
    if (tool.id === currentToolId) { setOpen(false); return; }
    const qs = buildContextParams(ctx);
    const url = qs ? `${tool.path}?${qs}` : tool.path;
    setOpen(false);
    navigate(url);
  };

  const grouped: Record<string, InAppTool[]> = {};
  for (const cat of IN_APP_CATEGORIES) {
    grouped[cat] = IN_APP_TOOLS.filter(t => t.category === cat);
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 4px',
          borderRadius: 4,
          fontSize: 'inherit',
          fontFamily: 'inherit',
          fontWeight: 'inherit',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
      >
        {currentTool?.title || currentToolId}
        <ChevronDown size={12} style={{ opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
      </button>

      {open && (
        <div style={dropdownStyle}>
          <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.03em' }}>
            Switch tool {ctx.moleculeId ? '(keeps molecule)' : ''}
          </div>
          <div style={{ height: 1, background: 'var(--card-border)', margin: '0 8px' }} />
          {IN_APP_CATEGORIES.map(cat => {
            const tools = grouped[cat];
            if (!tools?.length) return null;
            return (
              <div key={cat}>
                <div style={catLabelStyle}>{cat}</div>
                {tools.map(tool => (
                  <button
                    key={tool.id}
                    onClick={() => handleSelect(tool)}
                    style={{
                      ...itemStyle,
                      background: tool.id === currentToolId ? 'rgba(245,158,11,0.1)' : 'transparent',
                      color: tool.id === currentToolId ? '#f59e0b' : 'var(--text-primary)',
                    }}
                    onMouseEnter={e => {
                      if (tool.id !== currentToolId) (e.currentTarget as HTMLElement).style.background = 'var(--hover-bg)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = tool.id === currentToolId ? 'rgba(245,158,11,0.1)' : 'transparent';
                    }}
                  >
                    {tool.title}
                  </button>
                ))}
              </div>
            );
          })}
          <div style={{ height: 1, background: 'var(--card-border)', margin: '4px 8px' }} />
          <button
            onClick={() => { setOpen(false); navigate('/hubs'); }}
            style={{ ...itemStyle, color: '#f59e0b', fontWeight: 500, marginBottom: 4 }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--hover-bg)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            Browse all tools &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  minWidth: 220,
  maxHeight: 400,
  overflowY: 'auto',
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 10,
  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  zIndex: 100,
  paddingTop: 2,
  paddingBottom: 2,
};

const catLabelStyle: React.CSSProperties = {
  padding: '8px 12px 4px',
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const itemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '6px 12px 6px 20px',
  background: 'transparent',
  border: 'none',
  color: 'var(--text-primary)',
  fontSize: 13,
  cursor: 'pointer',
  borderRadius: 0,
  fontFamily: 'inherit',
};

export default ToolSwitcher;
